import { getTaxRateByRegion } from '@principle-theorem/accounting';
import {
  DestinationEntityRecordStatus,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityJobRunOptions,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Brand,
  TreatmentConfiguration,
} from '@principle-theorem/principle-core';
import {
  TreatmentConfigurationName,
  type IPatient,
  type IStaffer,
  type ITreatmentStep,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  getError,
  resolveSequentially,
  snapshotCombineLatest,
  TypeGuard,
  where,
  type WithRef,
} from '@principle-theorem/shared';
import { isArray } from 'lodash';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DestinationEntity } from '../../../destination/destination-entity';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientTreatmentPlanDestinationEntity,
  IPatientTreatmentPlanJobData,
  IPatientTreatmentPlanMigrationData,
  PATIENT_TREATMENT_PLAN_DESTINATION_ENTITY,
  PATIENT_TREATMENT_STEP_CUSTOM_MAPPING_TYPE,
} from '../../../destination/entities/patient-treatment-plans';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import { PracticeMigration } from '../../../practice-migrations';
import { TranslationMapHandler } from '../../../translation-map';
import {
  ExactPatient,
  PatientSourceEntity,
} from '../../source/entities/patient';
import { AppointmentSourceEntity } from '../../source/entities/patient-appointment';
import {
  ExactTreatment,
  ExactTreatmentType,
  PatientTreatmentSourceEntity,
} from '../../source/entities/patient-treatments';
import { ExactFeeScheduleMappingHandler } from '../mappings/fee-schedules';
import { ExactItemCodeToTreatmentMappingHandler } from '../mappings/item-code-to-treatment';
import { ExactItemCodeMappingHandler } from '../mappings/item-codes';
import {
  EXACT_APPOINTMENT_STEP_LINK_RESOURCE_TYPE,
  ExactStepIdentity,
} from './lib/exact-step-identity';
import { type IExactAppointmentStepLink } from './lib/exact-step-planner';
import { ExactTreatmentPlanBuilder } from './lib/exact-treatment-plan-builder';
import { PatientDestinationEntity } from './patient';
import { StafferDestinationEntity } from './staff';
import { ExactStafferMappingHandler } from '../mappings/staff';

export interface IExactPatientTreatmentPlanMigrationData
  extends IPatientTreatmentPlanMigrationData {
  appointmentLinks: IExactAppointmentStepLink[];
}

const patientTreatmentPlanDestinationEntity =
  DestinationEntity.withMetadataDescription(
    PATIENT_TREATMENT_PLAN_DESTINATION_ENTITY,
    `This migrates the treatments for each patient. Exact does have a loose structure for treatment plans, so we are going to migrate in similar fashion.
      - Treatments with a plan id will be grouped into a treatment plan using the Id (as they do in Exact) - a description is also used for naming if present
      - Treatments without a plan id will be placed in a default plan
      - Each plan gets a step per visit: completed treatments are grouped by completed date and appointment, planned treatments by their booked appointment or else by planned date and visit_sequence, and undated planned treatments share one step
      - Steps are ordered by date, and each appointment is linked to at most one step for the appointments migration to use

      Treatments all have a type in Exact:
      - 'Historic' treatments will be considered completed
      - 'Base' treatments are considered existing so should be treated like conditions?
      - 'Planned' are considered incomplete IF they have no appointment_id or the appointment is not complete
    `
  );

type JobData = IPatientTreatmentPlanJobData<ExactPatient>;

export class PatientTreatmentPlanDestinationEntity extends BasePatientTreatmentPlanDestinationEntity<
  ExactPatient,
  JobData
> {
  static hasAppointmentLinks = TypeGuard.interface<
    Pick<IExactPatientTreatmentPlanMigrationData, 'appointmentLinks'>
  >({ appointmentLinks: isArray });

  override destinationEntity = patientTreatmentPlanDestinationEntity;
  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    treatments: new PatientTreatmentSourceEntity(),
    appointments: new AppointmentSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
    staff: new StafferDestinationEntity(),
  };

  customMappings = {
    staff: new ExactStafferMappingHandler(),
    feeSchedules: new ExactFeeScheduleMappingHandler(),
    itemCodes: new ExactItemCodeMappingHandler(),
    itemCodeToTreatment: new ExactItemCodeToTreatmentMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<JobData[]> {
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const brand$ = PracticeMigration.brand$(migration);
    const practitioners$ = of([]);
    const taxRate$ = PracticeMigration.organisation$(migration).pipe(
      map((org) => getTaxRateByRegion(org.region))
    );
    const defaultTreatmentConfiguration$ = brand$.pipe(
      switchMap((brand) =>
        Firestore.firstResult(
          TreatmentConfiguration.col(brand),
          where('name', '==', TreatmentConfigurationName.GeneralADACode)
        )
      ),
      map((config) => {
        if (!config) {
          throw new Error(
            `Couldn't find default treatment configuration ${TreatmentConfigurationName.GeneralADACode}`
          );
        }
        return config;
      })
    );
    const treatmentCategories$ = brand$.pipe(
      switchMap((brand) => Brand.treatmentCategories$(brand))
    );
    const treatmentConfigurations$ = brand$.pipe(
      switchMap((brand) => TreatmentConfiguration.all$(brand))
    );

    return snapshotCombineLatest([
      staff$,
      practitioners$,
      defaultTreatmentConfiguration$,
      treatmentConfigurations$,
      this.customMappings.itemCodeToTreatment.getRecords(translationMap),
      this.customMappings.itemCodes.getRecords(translationMap),
      treatmentCategories$,
      taxRate$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([
          sourcePatients,
          [
            staff,
            practitioners,
            defaultTreatmentConfiguration,
            treatmentConfigurations,
            treatmentConfigurationMappings,
            sourceItemCodes,
            treatmentCategories,
            taxRate,
          ],
        ]) =>
          sourcePatients.map((sourcePatient) => ({
            sourcePatient,
            staff,
            practitioners,
            defaultTreatmentConfiguration,
            treatmentConfigurations,
            treatmentConfigurationMappings,
            sourceItemCodes,
            treatmentCategories,
            taxRate,
          }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: JobData
  ): Promise<
    | IExactPatientTreatmentPlanMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    const sourcePatientId = this.sourceEntities.patients.getSourceRecordId(
      data.sourcePatient.data
    );

    const patientRef = await translationMap.getDestination<IPatient>(
      sourcePatientId,
      PATIENT_RESOURCE_TYPE
    );

    if (!patientRef) {
      return this._buildErrorResponse(data.sourcePatient, 'No patient');
    }

    const treatments = await getExactPatientTreatments(
      this.sourceEntities.treatments,
      migration,
      sourcePatientId
    );

    const appointments = await this.sourceEntities.appointments.filterRecords(
      migration,
      'patientId',
      sourcePatientId
    );

    try {
      const { planPairs, appointmentLinks } =
        await ExactTreatmentPlanBuilder.buildTreatmentPlanData(
          data,
          translationMap,
          treatments,
          migration,
          sourcePatientId,
          appointments
        );

      return {
        sourcePatientId,
        patientRef,
        planPairs,
        appointmentLinks,
      };
    } catch (error) {
      return this._buildErrorResponse(data.sourcePatient, getError(error));
    }
  }

  /**
   * Once the plans are upserted, saves which step each appointment links to,
   * so the appointment job reads the link instead of recomputing step ids.
   * Links are read and written one at a time to keep this job's Postgres
   * connections low while many patient jobs share the pool.
   */
  override async runJob(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    jobData: JobData,
    migrationData: IPatientTreatmentPlanMigrationData
  ): Promise<IDestinationEntityRecord> {
    const result = await super.runJob(
      migration,
      destinationEntity,
      translationMap,
      jobData,
      migrationData
    );
    if (result.status !== DestinationEntityRecordStatus.Migrated) {
      return result;
    }

    // The merge-conflict runner passes user-edited data, which can lack the
    // appointment links. The saved links are then left as they are.
    if (
      !PatientTreatmentPlanDestinationEntity.hasAppointmentLinks(migrationData)
    ) {
      return result;
    }

    try {
      await this._saveAppointmentLinks(
        translationMap,
        migrationData.sourcePatientId,
        migrationData.appointmentLinks
      );
      return result;
    } catch (error) {
      return this._buildErrorResponse(jobData.sourcePatient, getError(error));
    }
  }

  private async _saveAppointmentLinks(
    translationMap: TranslationMapHandler,
    sourcePatientId: string,
    appointmentLinks: IExactAppointmentStepLink[]
  ): Promise<void> {
    await resolveSequentially(appointmentLinks, async (link) => {
      const stepRef = await translationMap.getDestination<ITreatmentStep>(
        link.stepSourceIdentifier,
        PATIENT_TREATMENT_STEP_CUSTOM_MAPPING_TYPE
      );
      if (!stepRef) {
        throw new Error(
          `No treatment step mapped for ${link.stepSourceIdentifier}`
        );
      }

      await translationMap.upsert({
        sourceIdentifier: ExactStepIdentity.appointmentLinkKey(
          sourcePatientId,
          link.appointmentUid
        ),
        destinationIdentifier: stepRef,
        resourceType: EXACT_APPOINTMENT_STEP_LINK_RESOURCE_TYPE,
      });
    });
  }
}

export async function getExactPatientTreatments(
  treatmentSourceEntity: PatientTreatmentSourceEntity,
  migration: WithRef<IPracticeMigration>,
  sourcePatientId: string
): Promise<IGetRecordResponse<ExactTreatment>[]> {
  return treatmentSourceEntity.filterRecords(
    migration,
    'patientId',
    sourcePatientId,
    (record) => record.data.treatment_type !== ExactTreatmentType.Base
  );
}
